// Shared types for the entitlement engine.

export const TIERS = ['starter', 'base', 'plus', 'pro', 'enterprise'] as const;
export type Tier = (typeof TIERS)[number];

export const TIER_RANK: Record<Tier, number> = {
  starter: 0,
  base: 1,
  plus: 2,
  pro: 3,
  enterprise: 4,
};

// Plan values as stored in billingDetails.plan (uppercase, matches DynamoDB)
export const PLANS = ['STARTER', 'BASE', 'PLUS', 'PRO', 'ENTERPRISE'] as const;
export type Plan = (typeof PLANS)[number];

// The Stripe Billing Meter event name for SMS sends (PE-1627). This is the
// CROSS-BACKEND CONTRACT: `backends/pricing` creates the Meter with this
// `event_name` and the matching graduated metered Price; `backends/marketing`
// reports usage with `billing.meterEvents.create({ event_name: SMS_METER_EVENT_NAME, ... })`.
// It lives in the engine (the shared library both backends embed) because
// dependency-cruiser forbids one backend importing another — a single literal
// here keeps the two sides from drifting. The Meter's payload contract is
// `customer_mapping.event_payload_key = 'stripe_customer_id'` and
// `value_settings.event_payload_key = 'value'` (pinned explicitly when the
// Meter is created — Stripe defaults, locked so the marketing payload matches).
export const SMS_METER_EVENT_NAME = 'sms_sent';

// Overage price per credit, in integer cents, beyond a plan's included SMS
// allotment (PE-1627; decisions.md 2026-06-25). 1 credit = $0.02 — the
// marketing handler reports credit COUNTS (SMS = num_segments, MMS = 2), so
// this single per-credit rate yields $0.02/SMS-segment and $0.04/MMS.
// SHARED CONTRACT like SMS_METER_EVENT_NAME above: `backends/pricing` stamps
// this rate into the graduated metered Price (unit_amount + metadata +
// idempotency key — a used Stripe Price is immutable, so changing this mints
// a fresh Price), and the dashboard's MessagingCredits card renders the same
// figure as the estimated overage cost (PE-1989 follow-up). A single literal
// here keeps what we bill and what we display from drifting.
export const SMS_OVERAGE_UNIT_AMOUNT_CENTS = 2;

export const PLAN_TO_TIER: Record<Plan, Tier> = {
  STARTER: 'starter',
  BASE: 'base',
  PLUS: 'plus',
  PRO: 'pro',
  ENTERPRISE: 'enterprise',
};

// Source tri-state (from feature-gating-source.yaml)
export type TriState = 'included' | 'roadmap' | 'none' | 'custom';

// Runtime boolean grid: feature key → tier → on/off
export type BooleanGrid = Record<string, Record<Tier, boolean>>;

// Usage limits in the generated catalog
export type LimitValue = number | 'unlimited' | null; // null = not available on tier
export type LimitsGrid = Record<string, Record<Tier, LimitValue>>;

// Fee rates as exact decimal strings (no float math in the lib)
export type FeeRates = {
  platformFeeCovered: Record<Tier, string>;   // e.g. "0.07"
  platformFeeUncovered: Record<Tier, string>;
  processingFee: Record<Tier, string>;        // flat 3% across all tiers
  // Per-transaction FIXED fees as INTEGER CENTS (NOT decimal strings) — a fixed
  // fee is a dollar amount, not a rate, so it rides the subscription-price
  // representation (integer cents, no parse/round on the money path), NOT the
  // 4-decimal percent path. 0 across all standard tiers today (bill-neutral);
  // null = enterprise/custom (no list price — the consumer uses its stored
  // negotiated value, never the catalog). PE-1630 §1.5x.
  platformFixedFee: Record<Tier, number | null>;
  processingFixedFee: Record<Tier, number | null>;
};

// Subscription list prices as INTEGER CENTS — consumed directly as a Stripe
// `unit_amount` (which must be an integer, no float math). `monthlyUsd` is the
// month-to-month price; `yearlyMonthlyUsd` is the per-month price when billed
// annually (the value Stripe charges monthly on an annual plan). null = no list
// price: starter is $0 (so 0), enterprise is per-contract custom (so null →
// consumers must reject, never charge). See pricing-backend-implementation-plan.md
// Stage 0/4.
export type SubscriptionPrices = {
  monthlyUsd: Record<Tier, number | null>;
  yearlyMonthlyUsd: Record<Tier, number | null>;
};

// Free-trial length in DAYS per tier. Consumed directly as a Stripe Checkout
// Session `subscription_data.trial_period_days` (which must be a positive
// integer; 0/absent means no trial). Plain integers — no cents transform.
// Base = 30 (card-required trial, decisions.md 2026-06-22); all others = 0.
export type TrialDays = Record<Tier, number>;

// Fee-mode of an override profile. `negotiated` means the profile carries a
// per-org negotiated fee deal (percent AND fixed) stored on the org's
// billingDetails.feeAmounts — the engine echoes that stored rate rather than
// deriving from the catalog. Absent from this map ⇒ the profile is NOT
// fee-negotiated and the org derives its fee from the catalog like any standard
// tier. This is CATALOG-DRIVEN (data, not a hardcoded string in the resolver):
// `feeMode: negotiated` lives on the profile in feature-gating-source.yaml, so
// which bundles are fee-negotiated is a YAML row, not resolver code. PE-1700.
export type OverrideFeeMode = 'negotiated';
export type OverrideFeeModeMap = Record<string, OverrideFeeMode>;

// The generated catalog — produced by the generator, imported by the resolver
export type Catalog = {
  features: BooleanGrid;
  limits: LimitsGrid;
  fees: FeeRates;
  subscriptionPrices: SubscriptionPrices;
  trialDays: TrialDays;
  // Names of all features that are in the "marketing" group (used for grandfather profiles)
  marketingFeatureKeys: ReadonlySet<string>;
  // All feature keys that are live (included at any tier)
  allLiveFeatureKeys: ReadonlySet<string>;
  // override profile name → fee mode. Only profiles with a fee deal appear here
  // (today: legacy_custom → 'negotiated'). PE-1700.
  overrideFeeModes: OverrideFeeModeMap;
};

// Override profiles for grandfathered / legacy orgs
export const OVERRIDE_PROFILES = [
  'legacy_starter',
  'legacy_custom',
  'grandfathered_base',
  'plus_grandfathered',
] as const;
export type OverrideProfile = (typeof OVERRIDE_PROFILES)[number];

// The stored negotiated rate, as it lives on the org record's `feeAmounts`.
// STRING decimal rates (the same 4-decimal form the catalog percents use) and
// STRING dollar amounts for the fixed fees — matching DynamoDB's stored shape,
// where negotiated orgs carry e.g. processingPercentFee "0.029" / processingFixedFee
// "0.30". All fields OPTIONAL: a negotiated org may carry only some components
// (e.g. platformPercentFee "0" with all the rate in processingPercentFee).
//
// IMPORTANT (PE-1700): on the org RECORD, `feeAmounts` sits at TOP LEVEL, a
// sibling of `billingDetails` — NOT nested inside billingDetails. The engine
// reads it from the OrgBillingDetails input it is handed, so the CALLER is
// responsible for lifting the org's top-level `feeAmounts` into this input
// before calling resolveFees. Reading `billing.feeAmounts` here is field access
// on an already-passed in-memory input (NOT I/O) — the domain stays pure.
export interface StoredFeeAmounts {
  platformPercentFee?: string | number;
  processingPercentFee?: string | number;
  platformFixedFee?: string | number;
  processingFixedFee?: string | number;
}

// The org's billing state — what the resolver reads
export interface OrgBillingDetails {
  // `plan` is SPARSE — written only when an org has a Stripe subscription, so it is
  // OPTIONAL here. An absent plan resolves to implicit STARTER (architecture §6);
  // most orgs (and every not-yet-on-a-paid-tier org) legitimately have no plan.
  plan?: Plan | null;
  entitlementOverrides?: OverrideProfile | null;
  coveredCommitment?: boolean;
  // NEW (PE-1700): the stored negotiated rate. Echoed VERBATIM (percent AND fixed)
  // when the org's entitlementOverrides profile is fee-negotiated (legacy_custom).
  // The caller lifts the org record's top-level `feeAmounts` into here. On a
  // catalog-modeled org this is ignored (the engine derives from the catalog).
  feeAmounts?: StoredFeeAmounts | null;
  // processingFees.tier is the FEE DEAL axis — NEVER read for entitlement.
  // It is intentionally typed as `unknown` to discourage accidental reads.
  processingFees?: { tier?: unknown };
}

// Resolved entitlements returned by the resolver
export interface ResolvedEntitlements {
  features: ReadonlySet<string>;
  limits: Record<string, LimitValue>;
  fees: ResolvedFees;
}

// The COMPLETE resolved fee number the engine owns (PE-1700). Percents are
// 4-decimal strings for a catalog-derived org (no float math); for a negotiated
// org they are the stored rate echoed VERBATIM (whatever string/number was
// stored). Fixed fees are INTEGER CENTS for a negotiated org (the stored dollar
// amount converted, e.g. "0.30" → 30) and for every fee-modeled catalog tier
// (STARTER/BASE/PLUS/PRO, all 0 cents today). PE-1700 closed the old
// `feeOverride` deal-path hole where the negotiated path hardcoded a null fixed
// fee — a negotiated org now always yields a numeric fixed fee.
//
// `platformFixed`/`processingFixed` stay `number | null` because the `enterprise`
// catalog tier carries a `null` catalog fixed fee: on that derive path the engine
// returns null, and TypeScript cannot narrow it away. Consumers MUST null-check
// these (coalesce to 0 / handle the enterprise case) — do NOT rely on a non-null
// assertion.
export interface ResolvedFees {
  platformPct: string;
  processingPct: string;
  platformFixed: number | null;
  processingFixed: number | null;
}
